// src/pages/AuthPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../apiClient";
import "./AuthStyles.css";

function AuthPage() {
  const [mode, setMode] = useState("login"); // login or signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [motiveApiKey, setMotiveApiKey] = useState("");
  const [showExtraForm, setShowExtraForm] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [checkingInvite, setCheckingInvite] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const navigate = useNavigate();

  const [redirectPending, setRedirectPending] = useState(
    localStorage.getItem("googleRedirectPending") === "true"
  );

  // AuthContext
  const {
    user,
    login,
    signUp,
    signInWithGoogle,
    completeSignup,
    resetPassword,
    loading,
    error,
    clearError,
  } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get("ok");
    const error = params.get("error");

    if (!ok && !error) {
      // Not an OAuth redirect → clear stuck state
      localStorage.removeItem("googleRedirectPending");
      setRedirectPending(false);
    }

    if (ok === "true") {
      localStorage.removeItem("googleRedirectPending");
      setRedirectPending(false);
      navigate("/");
    }

    if (error) {
      localStorage.removeItem("googleRedirectPending");
      setRedirectPending(false);
    }
  }, [navigate]);

  // Handle OAuth redirects (backend → frontend)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get("ok");
    const error = params.get("error");
    const companyId = params.get("companyId");

    if (ok === "true") {
      // Successful OAuth login/signup
      localStorage.setItem("authSuccessMessage", "Signed in successfully!");
      if (companyId) {
        localStorage.setItem("companyId", companyId);
      }
      window.history.replaceState({}, "", "/"); // clean URL
      navigate("/", { replace: true });
    }

    if (error) {
      // OAuth failed
      setSuccessMessage("");
      localStorage.removeItem("googleRedirectPending");
      setRedirectPending(false);
      window.history.replaceState({}, "", "/auth"); // clean URL but stay on auth page
    }
  }, [navigate]);

  // Check for success message from localStorage on mount
  useEffect(() => {
    const storedMessage = localStorage.getItem('authSuccessMessage');
    if (storedMessage) {
      setSuccessMessage(storedMessage);
      localStorage.removeItem('authSuccessMessage');
      // Start auto-hide timer
      const timer = setTimeout(() => setSuccessMessage(""), 10000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Auto-hide success message after 10 seconds (for non-signup messages)
  useEffect(() => {
    if (successMessage && successMessage !== 'Account created successfully! Please check your email to verify your account before logging in.') {
      const timer = setTimeout(() => setSuccessMessage(""), 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !user.needsSignup) {
      navigate("/", { replace: true });
    } else if (user?.needsSignup) {
      setShowExtraForm(true);
      setFullName(user.displayName || "");
    }
  }, [user, navigate]);

  useEffect(() => {
    // Skip if not in signup mode or if showing extra info (e.g., Google flow)
    if (mode !== "signup" || showExtraForm) {
      return;
    }

    // Skip if email is empty or invalid
    if (!email || !email.includes("@")) {
      setInvitationInfo(null);
      return;
    }

    let isCancelled = false; // prevent state updates after unmount
    setCheckingInvite(true);

    const timeout = setTimeout(async () => {
      try {
        const response = await api.post("/auth/check-invitation", { email });
        if (!isCancelled) {
          setInvitationInfo(response.data);
        }
      } catch (err) {
        if (!isCancelled) {
          setInvitationInfo({ hasInvite: false });
        }
      } finally {
        if (!isCancelled) setCheckingInvite(false);
      }
    }, 600); // small debounce (600ms)

    return () => {
      isCancelled = true;
      clearTimeout(timeout);
    };
  }, [email, mode, showExtraForm]);


  

  if ((loading && !successMessage) || redirectPending) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <p>{redirectPending ? "Signing you in with Google..." : "Loading..."}</p>
        </div>
      </div>
    );
  }


  // Email login/signup
  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setSuccessMessage("");

    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/", { replace: true });
      } else {
        if (!fullName.trim()) throw new Error("Full name is required");
        if (invitationInfo && !invitationInfo?.hasInvite) {
          if (!companyName.trim()) throw new Error("Company name is required");
          if (!motiveApiKey.trim()) throw new Error("Motive API key is required");
        }

        const result = await signUp(email, password, {
          fullName,
          phoneNumber,
          companyName,
          motiveApiKey,
        });

        // Show success message instead of alert
        if (result?.emailSent) {
          // Store message in localStorage to survive the page reload
          localStorage.setItem('authSuccessMessage', 'Account created successfully! Please check your email to verify your account before logging in.');
          // Force a full page reload to reset all state cleanly
          window.location.href = '/auth';
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Google login/signup - FIXED VERSION
  const handleGoogleSignIn = async () => {
    clearError();
    setSuccessMessage("");
    try {
      localStorage.setItem("googleRedirectPending", "true");
      const result = await signInWithGoogle();      
      if (!result) return; // redirect for mobile

      if (result.status === "needs-info") {
        setFullName(result.googleUser.displayName || "");

        let inviteStatus = null;
        if (result.googleUser.email) {
          setCheckingInvite(true);
          try {
            const response = await api.post("/auth/check-invitation", { email: result.googleUser.email });
            inviteStatus = response.data;
          } catch (err) {
            console.error("[GOOGLE SIGNIN] Error checking invitation:", err);
          } finally {
            setCheckingInvite(false);
          }
        }
        
        setInvitationInfo(inviteStatus);
        setShowExtraForm(true);
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("[GOOGLE SIGNIN] Error:", err);
    }
  };

  // Submit extra info for Google signup - UPDATED
  const submitExtraInfo = async () => {
    clearError();
    setSuccessMessage("");
    try {
      if (!fullName.trim()) throw new Error("Full name is required");
      
      // Only validate company/API key if user is NOT invited
      if (invitationInfo && !invitationInfo?.hasInvite) {
        if (!companyName.trim()) throw new Error("Company name is required");
        if (!motiveApiKey.trim()) throw new Error("Motive API key is required");
      }

      await completeSignup({
        fullName,
        phoneNumber,
        // Send empty strings for invited users, actual values for non-invited
        companyName: invitationInfo?.hasInvite ? "" : companyName,
        motiveApiKey: invitationInfo?.hasInvite ? "" : motiveApiKey,
      });

      setShowExtraForm(false);
      navigate("/", { replace: true });
    } catch (err) {
      console.error(err);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!email) {
      setSuccessMessage("");
      clearError();
      throw new Error("Please enter your email address");
    }
    try {
      await resetPassword(email);
      setResetEmailSent(true);
      setSuccessMessage("Password reset email sent! Check your inbox.");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {!showExtraForm ? (
          <>
            <div className="auth-header">
              <h2 className="auth-title">{mode === "login" ? "Sign in" : "Sign up"}</h2>
              <p className="auth-subtitle">{mode === "login" ? "Welcome back to FX Fleet Reports" : "Create your account"}</p>
            </div>

            <button
              className="social-button google-button"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              G {mode === "login" ? "Sign in with Google" : "Sign up with Google"}
            </button>

            <div className="divider"><span>Or</span></div>

            <form onSubmit={handleSubmit}>
              {mode === "signup" && (
                <>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  {invitationInfo && !invitationInfo.hasInvite && (
                    <>
                      <div className="form-group">
                        <label>Company Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                      <div className="form-group">
                        <label>Motive API Key *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={motiveApiKey}
                          onChange={(e) => setMotiveApiKey(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    </>
                  )}

                </>
              )}

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
                {checkingInvite && mode === "signup" && (
                  <p style={{ fontSize: 12, color: "#718096" }}>Checking invitation...</p>
                )}
                {invitationInfo?.hasInvite && (
                  <p style={{ fontSize: 14, color: "#22543D", background: "#C6F6D5", padding: 8, borderRadius: 4 }}>
                    ✓ You have been invited to join {invitationInfo.companyName}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                {mode === "login" && (
                  <button type="button" className="link-button" onClick={handleResetPassword} disabled={loading}>
                    Forgot password?
                  </button>
                )}
                {resetEmailSent && <p style={{ color: "#2d3748" }}>Password reset sent!</p>}
              </div>

              <button className="submit-button" type="submit" disabled={loading}>
                {loading ? "Processing..." : mode === "login" ? "Sign in" : "Sign up"}
              </button>
            </form>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <div className="auth-footer">
              <p>
                {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
                <button
                  className="link-button"
                  onClick={() => {
                    setMode(mode === "login" ? "signup" : "login");
                    setResetEmailSent(false);
                    setSuccessMessage("");
                  }}
                  disabled={loading}
                >
                  {mode === "login" ? "Sign up" : "Sign in"}
                </button>
              </p>
            </div>
          </>
        ) : (
          // Extra info form for Google signup
          <>
            <div className="auth-header">
              <h2 className="complete-signup-title">Complete Signup</h2>
            </div>

            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                className="form-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                className="form-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={loading}
              />
            </div>
            {invitationInfo && !invitationInfo?.hasInvite && (
              <>
                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Motive API Key *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={motiveApiKey}
                    onChange={(e) => setMotiveApiKey(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </>
            )}

            <button className="submit-button" onClick={submitExtraInfo} disabled={loading}>
              {loading ? "Completing signup..." : "Complete Signup"}
            </button>

            {error && <div className="error-message">{error}</div>}
          </>
        )}
      </div>
    </div>
  );
}

export default AuthPage;
